import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Callout,
    EmptyState,
    FormField,
    Modal,
    Select,
    Skeleton,
    useToast,
} from '@/components/ui';
import { useAuth } from '@/auth/useAuth';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import {
    fetchOrgMovePreview,
    fetchOrganizations,
    fetchOrgUnitTypes,
    moveOrganization,
    type Organization,
    type OrgMovePreview,
    type OrgMoveUnitTypePlanEntry,
} from '@/api/orgAdmin';
import { describeMoveFailure, translateMoveBlocker } from './moveMessages';

/**
 * EAA-1891: "Chuyển vào tổ chức khác" — take an organization (typically one that
 * is currently a ROOT) and file it in under an organization in a DIFFERENT tree,
 * carrying its whole subtree.
 *
 * Design: Claude Design project "UI Portal App Shell Redesign", frames
 * `21b Move dialog` / `21c Move dialog blocked` (file
 * `Portal 4b Chuyen to chuc goc.dc.html`).
 *
 * WHY THIS IS NOT `console/hierarchy/MoveOrgDialog`, and why both stay. That
 * dialog drives `PATCH /organizations/{id}` with a new `parent` — the hierarchy
 * screen's drag-and-drop write, which the server still REFUSES across trees
 * because a drag landing on another tree is almost always a mis-drop. This one
 * drives `POST /organizations/{id}/move`, the deliberate act, which additionally
 * migrates the moved subtree's unit-type vocabulary into the destination root's.
 * Sharing one component would mean one dialog whose consequences depend on a
 * hidden mode flag; sharing one endpoint would make the drag path silently
 * capable of restructuring two trees at once.
 *
 * WHAT IS GRADED WHERE, and why the candidate list is barely graded at all.
 * `dragRules.classifyMoveCandidate` greys out cross-tree candidates — correct for
 * the tree, exactly wrong here, since cross-tree IS the point. Client-side this
 * component therefore rules out only what it can know for certain from the row in
 * hand (itself, its current parent, an inactive org); every structural rule
 * (cycle, unit-type containment) is answered by `move-preview`, which grades the
 * SAME plan the write executes, so preview and write cannot disagree. That also
 * means the descendants/accounts figures are the server's, not a client estimate.
 *
 * The preview writes nothing, so it is safe to re-run on every target the admin
 * highlights, and re-running it is how the blocker panel stays honest after a
 * unit-type choice.
 */

export interface MoveOrganizationModalProps {
    open: boolean;
    /** The org being moved; `null` closes the dialog (kept for exit animation parity). */
    organization: Organization | null;
    onClose: () => void;
    /** Called after a successful move, with the updated row. */
    onMoved?: (organization: Organization) => void;
}

type BlockedReason = 'self' | 'current-parent' | 'inactive';

interface Candidate {
    organization: Organization;
    blockedBy: BlockedReason | null;
}

const CANDIDATE_PAGE_SIZE = 50;

function planActionLabel(t: TFunction, entry: OrgMoveUnitTypePlanEntry) {
    switch (entry.action) {
        case 'merge_into_existing':
            return {
                tag: t('admin.organizations.move.actionMerge'),
                tone: 'merge' as const,
                detail: t('admin.organizations.move.actionMergeDetail', {
                    dest: entry.dest_type_name,
                }),
            };
        case 'reown':
            return {
                tag: t('admin.organizations.move.actionReown'),
                tone: 'reown' as const,
                detail: t('admin.organizations.move.actionReownDetail'),
            };
        default:
            return {
                tag: t('admin.organizations.move.actionClone'),
                tone: 'clone' as const,
                detail: t('admin.organizations.move.actionCloneDetail'),
            };
    }
}

export function MoveOrganizationModal({
    open,
    organization,
    onClose,
    onMoved,
}: MoveOrganizationModalProps) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const queryClient = useQueryClient();
    const toast = useToast();

    const [query, setQuery] = useState('');
    const [targetId, setTargetId] = useState<string | null>(null);
    const [unitTypeId, setUnitTypeId] = useState<string | null>(null);

    // Reopening for a DIFFERENT org must not inherit the previous one's target —
    // a stale target here would preview (and then move) into the wrong tree.
    useEffect(() => {
        if (!open) return;
        setQuery('');
        setTargetId(null);
        setUnitTypeId(null);
    }, [open, organization?.id]);

    const trimmed = query.trim();
    const enabled = open && Boolean(accessToken) && Boolean(organization);

    const candidatesQuery = useQuery({
        queryKey: ['org-move-candidates', accessToken, trimmed],
        queryFn: () =>
            fetchOrganizations(config.apiBase, accessToken as string, {
                ...(trimmed ? { search: trimmed } : {}),
                pageSize: CANDIDATE_PAGE_SIZE,
                ordering: 'name',
            }),
        enabled,
        retry: false,
    });

    const candidates = useMemo<Candidate[]>(() => {
        const rows = candidatesQuery.data?.results ?? [];
        if (!organization) return [];
        return rows.map((row) => {
            let blockedBy: BlockedReason | null = null;
            if (row.id === organization.id) blockedBy = 'self';
            else if (row.id === organization.parent) blockedBy = 'current-parent';
            else if (!row.is_active) blockedBy = 'inactive';
            return { organization: row, blockedBy };
        });
    }, [candidatesQuery.data, organization]);

    const previewQuery = useQuery({
        queryKey: [
            'org-move-preview',
            accessToken,
            organization?.id ?? null,
            targetId,
            unitTypeId,
        ],
        queryFn: () =>
            fetchOrgMovePreview(config.apiBase, accessToken as string, organization?.id as string, {
                parentId: targetId as string,
                ...(unitTypeId ? { unitTypeId } : {}),
            }),
        enabled: enabled && Boolean(targetId),
        retry: false,
    });
    const preview: OrgMovePreview | null = previewQuery.data ?? null;

    /**
     * The DESTINATION root's vocabulary, for the "loại đơn vị sau khi chuyển"
     * picker. Keyed off `preview.target_root`, not off the chosen target: the
     * vocabulary belongs to the root, and the chosen target is usually a child of
     * it. `activeOnly` is true here — this is a picker for a new assignment, and a
     * retired type must not be handed out afresh.
     */
    const targetRootId = preview?.target_root.id ?? null;
    const unitTypesQuery = useQuery({
        queryKey: ['org-unit-types', accessToken, targetRootId, true],
        queryFn: () =>
            fetchOrgUnitTypes(config.apiBase, accessToken as string, targetRootId as string, true),
        enabled: enabled && Boolean(targetRootId),
        retry: false,
    });

    const blockers = preview?.blockers ?? [];
    const containmentBlocked = blockers.some(
        (b) => b.rule === 'unit_type_containment' || b.rule === 'unknown_unit_type'
    );
    const canConfirm = Boolean(targetId) && Boolean(preview) && blockers.length === 0;

    const moveMutation = useMutation({
        mutationFn: () =>
            moveOrganization(config.apiBase, accessToken as string, organization?.id as string, {
                parent_id: targetId as string,
                ...(unitTypeId ? { unit_type_id: unitTypeId } : {}),
            }),
        onSuccess: (moved) => {
            toast.success(
                t('admin.organizations.move.successToast', {
                    name: moved.name,
                    target: preview?.target_parent.name ?? '',
                })
            );
            // Both trees changed shape, and so did the destination root's unit-type
            // vocabulary — invalidate the org lists, the tree levels the hierarchy
            // screen caches, and every unit-type list (the destination gained rows,
            // the source may have lost or retired some).
            void queryClient.invalidateQueries({ queryKey: ['organizations-list'] });
            void queryClient.invalidateQueries({ queryKey: ['org-children'] });
            void queryClient.invalidateQueries({ queryKey: ['org-unit-types'] });
            void queryClient.invalidateQueries({ queryKey: ['org-move-candidates'] });
            onMoved?.(moved);
            onClose();
        },
        onError: (error) => toast.error(describeMoveFailure(t, error)),
    });

    const footerNote = !targetId
        ? t('admin.organizations.move.footerPickTarget')
        : blockers.length > 0
          ? t('admin.organizations.move.blockerCount', { count: blockers.length })
          : t('admin.organizations.move.footerNote', {
                name: organization?.name ?? '',
                target: preview?.target_parent.name ?? '',
            });

    return (
        <Modal
            open={open}
            onCancel={onClose}
            width={860}
            title={
                organization
                    ? t('admin.organizations.move.title', { name: organization.name })
                    : ''
            }
            footer={
                <div className="portal-org-move-footer">
                    <span
                        className="portal-org-move-footer-note"
                        data-tone={blockers.length > 0 ? 'danger' : undefined}
                    >
                        {footerNote}
                    </span>
                    <span className="portal-org-move-footer-actions">
                        <button type="button" className="portal-btn" onClick={onClose}>
                            {t('common.cancel')}
                        </button>
                        <button
                            type="button"
                            className="portal-btn portal-btn-primary"
                            disabled={!canConfirm || moveMutation.isPending}
                            onClick={() => moveMutation.mutate()}
                        >
                            {t('admin.organizations.move.confirmButton')}
                        </button>
                    </span>
                </div>
            }
        >
            <p className="portal-org-move-lead">{t('admin.organizations.move.description')}</p>

            <div className="portal-org-move-body">
                <div className="portal-org-move-main">
                    <FormField
                        label={t('admin.organizations.move.targetLabel')}
                        hint={t('admin.organizations.move.targetHint')}
                    >
                        <div className="portal-org-move-picker">
                            <label className="portal-org-move-search">
                                <svg
                                    viewBox="0 0 24 24"
                                    width="15"
                                    height="15"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth={1.8}
                                    aria-hidden="true"
                                >
                                    <circle cx="11" cy="11" r="7" />
                                    <path d="m20 20-3.5-3.5" strokeLinecap="round" />
                                </svg>
                                <input
                                    type="text"
                                    value={query}
                                    onChange={(event) => setQuery(event.target.value)}
                                    placeholder={t(
                                        'admin.organizations.move.searchPlaceholder'
                                    )}
                                    aria-label={t('admin.organizations.move.searchAriaLabel')}
                                />
                            </label>
                            <div
                                className="portal-org-move-list"
                                role="listbox"
                                aria-label={t('admin.organizations.move.listAriaLabel')}
                            >
                                {candidatesQuery.isLoading &&
                                    [0, 1, 2].map((i) => (
                                        <span key={i} className="portal-org-move-skeleton">
                                            <Skeleton height="22px" width="22px" />
                                            <Skeleton
                                                height="0.75rem"
                                                width={`${52 - i * 9}%`}
                                            />
                                        </span>
                                    ))}
                                {candidatesQuery.isSuccess && candidates.length === 0 && (
                                    <EmptyState
                                        title={t(
                                            'admin.organizations.move.noCandidatesTitle'
                                        )}
                                    />
                                )}
                                {candidates.map(({ organization: row, blockedBy }) => {
                                    const blocked = blockedBy !== null;
                                    const chosen = row.id === targetId;
                                    const reason =
                                        blockedBy === 'self'
                                            ? t('admin.organizations.move.blockedSelf')
                                            : blockedBy === 'current-parent'
                                              ? t(
                                                    'admin.organizations.move.blockedCurrentParent'
                                                )
                                              : blockedBy === 'inactive'
                                                ? t('admin.organizations.move.blockedInactive')
                                                : null;
                                    return (
                                        <button
                                            key={row.id}
                                            type="button"
                                            role="option"
                                            aria-selected={chosen}
                                            className="portal-org-move-row"
                                            data-blocked={blocked || undefined}
                                            data-chosen={(chosen && !blocked) || undefined}
                                            disabled={blocked}
                                            onClick={() => {
                                                setTargetId(row.id);
                                                // A type chosen for the previous
                                                // target belongs to that target's
                                                // root, so it cannot carry over.
                                                setUnitTypeId(null);
                                            }}
                                        >
                                            <span className="portal-org-move-avatar">
                                                {row.code.slice(0, 2).toUpperCase()}
                                            </span>
                                            <span className="portal-org-move-name">
                                                {row.name}
                                            </span>
                                            {row.parent === null && (
                                                <span className="portal-org-move-root">
                                                    {t('admin.organizations.move.rootBadge')}
                                                </span>
                                            )}
                                            {reason ? (
                                                <span className="portal-org-move-reason">
                                                    {reason}
                                                </span>
                                            ) : (
                                                <code className="portal-org-move-code">
                                                    {row.code}
                                                </code>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </FormField>

                    {containmentBlocked && (
                        <FormField
                            label={t('admin.organizations.move.unitTypeLabel')}
                            required
                            error={
                                unitTypeId
                                    ? undefined
                                    : t('admin.organizations.move.unitTypeRequired')
                            }
                            hint={t('admin.organizations.move.unitTypeHint')}
                        >
                            <Select
                                value={unitTypeId ?? undefined}
                                onChange={(value: string) => setUnitTypeId(value)}
                                placeholder={t(
                                    'admin.organizations.move.unitTypePlaceholder'
                                )}
                                status={unitTypeId ? undefined : 'error'}
                                loading={unitTypesQuery.isLoading}
                                style={{ width: '100%' }}
                                options={(unitTypesQuery.data ?? []).map((type) => ({
                                    value: type.id,
                                    label: type.name,
                                }))}
                            />
                        </FormField>
                    )}
                    {containmentBlocked && unitTypesQuery.isError && (
                        <Callout tone="warning">
                            {t('admin.organizations.move.unitTypeLoadError')}
                        </Callout>
                    )}
                </div>

                <div className="portal-org-move-side">
                    {previewQuery.isError && (
                        <Callout tone="warning">
                            {t('admin.organizations.move.previewError')}
                        </Callout>
                    )}

                    {blockers.length > 0 && (
                        <div className="portal-org-move-blockers">
                            <span className="portal-org-move-side-key" data-tone="danger">
                                {t('admin.organizations.move.blockersTitle')}
                            </span>
                            <ul className="portal-org-move-blocker-list">
                                {blockers.map((blocker) => (
                                    <li key={blocker.rule}>{translateMoveBlocker(t, blocker)}</li>
                                ))}
                            </ul>
                            <p className="portal-org-move-side-note" data-tone="danger">
                                {t('admin.organizations.move.blockersHint')}
                            </p>
                        </div>
                    )}

                    <div className="portal-org-move-impact">
                        <span className="portal-org-move-side-key">
                            {t('admin.organizations.move.impactTitle')}
                        </span>
                        {previewQuery.isLoading && <Skeleton height="3rem" width="100%" />}
                        {preview && (
                            <>
                                <span className="portal-org-move-metric">
                                    <strong>
                                        {preview.descendants.toLocaleString('vi-VN')}
                                    </strong>
                                    {t('admin.organizations.move.impactDescendants')}
                                </span>
                                <span className="portal-org-move-metric">
                                    <strong>
                                        {preview.accounts_affected.toLocaleString('vi-VN')}
                                    </strong>
                                    {t('admin.organizations.move.impactAccounts')}
                                </span>
                                <p className="portal-org-move-side-note">
                                    {t('admin.organizations.move.impactAccountsNote')}
                                </p>
                            </>
                        )}
                        {!preview && !previewQuery.isLoading && (
                            <p className="portal-org-move-side-note">
                                {t('admin.organizations.move.previewNote')}
                            </p>
                        )}
                    </div>

                    {preview && (
                        <div className="portal-org-move-impact">
                            <span className="portal-org-move-side-key">
                                {t('admin.organizations.move.unitTypesTitle')}
                            </span>
                            {preview.unit_types_merged.length === 0 ? (
                                <p className="portal-org-move-side-note">
                                    {t('admin.organizations.move.unitTypesNone')}
                                </p>
                            ) : (
                                <>
                                    <ul className="portal-org-move-plan">
                                        {preview.unit_types_merged.map((entry) => {
                                            const label = planActionLabel(t, entry);
                                            return (
                                                <li key={entry.source_type_id}>
                                                    <span
                                                        className="portal-org-move-plan-tag"
                                                        data-action={label.tone}
                                                    >
                                                        {label.tag}
                                                    </span>
                                                    <span>
                                                        <strong>{entry.name}</strong>{' '}
                                                        {label.detail}
                                                    </span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                    <p className="portal-org-move-side-note">
                                        {t('admin.organizations.move.unitTypesNote')}
                                    </p>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
}

export default MoveOrganizationModal;
